import { useState, useCallback } from 'react';
import { useLessonCompletion } from '../level/useLessonCompletion';
import { dailyQuestService } from '../../services/DailyQuestService';
import { Lesson } from '@/models/Lesson';
import { MutationHooks } from '../useLevelProgression.types';

/**
 * Hook to manage daily quest completion functionality
 */
export const useDailyQuestCompletion = () => {
  const { completeLessonAndAwardXP } = useLessonCompletion();
  const [isCompletingQuest, setIsCompletingQuest] = useState(false);

  /**
   * Complete a daily quest and award XP (including the bonus)
   */
  const completeDailyQuestAndAwardXP = useCallback(
    async (
      userId: string | undefined,
      selectedLesson: Lesson | null,
      totalXpGained: number,
      setShowLessonComplete: React.Dispatch<React.SetStateAction<boolean>>,
      mutations: MutationHooks,
      isPerfectLesson: boolean = false,
      handleImperfectLesson?: () => void,
      updateLessonBonus?: (isPerfect: boolean) => number
    ): Promise<number> => {
      if (!userId || !selectedLesson) return 0;
      
      setIsCompletingQuest(true);
      
      // We'll calculate the XP directly rather than calling the lesson completion again
      // since that's already being called in the progression hook
      const levelCount = selectedLesson.levels?.length || 1;
      const BASE_XP_PER_LEVEL = 5;
      const regularXp = levelCount * BASE_XP_PER_LEVEL;
      
      console.log(`🎯 [Daily Quest] Base XP calculation: ${levelCount} levels × ${BASE_XP_PER_LEVEL} XP = ${regularXp}`);
      
      // Now handle the daily quest specific bonus and logging
      const DAILY_QUEST_BONUS = (selectedLesson as any).bonusXp || 25; // Use quest-specific bonus if available
      const finalXp = regularXp + DAILY_QUEST_BONUS;
      
      console.log(`🎯 [Daily Quest] Completing quest: ${selectedLesson.name}`);
      console.log(`🎯 [Daily Quest] Base XP: ${regularXp} + ${DAILY_QUEST_BONUS} bonus = ${finalXp} total XP`);
      
      // Set global variables for UI
      (window as any).__DAILY_QUEST_COMPLETED__ = true;
      (window as any).__DAILY_QUEST_BONUS__ = DAILY_QUEST_BONUS;
      
      // Update user profile with the additional XP
      if (userId && mutations.profileMutation) {
        try {
          await mutations.profileMutation.mutateAsync({
            userId: userId,
            updates: { xp: finalXp } // Add the total XP since we're directly handling quest completion
          });
          console.log(`🎯 [Daily Quest] Added ${finalXp} XP to user profile`);
        } catch (error) {
          console.error('Error updating user profile with quest XP:', error);
        }
      }
      
      // Log daily quest completion activity
      if (userId && selectedLesson.id && selectedLesson.dailyQuestType) {
        try {
          await mutations.activityMutation.mutateAsync({
            userId: userId,
            type: 'daily-quest-completed',
            lessonId: selectedLesson.id,
            lessonName: selectedLesson.name,
            xpEarned: finalXp,
            isDailyQuest: true,
            dailyQuestType: selectedLesson.dailyQuestType,
            dailyQuestBonus: DAILY_QUEST_BONUS,
            isPerfect: isPerfectLesson,
            isPerfectLesson: isPerfectLesson
          });
          console.log(`🎯 [Daily Quest] Activity logged successfully`);
        } catch (error) {
          console.error('Error logging daily quest activity:', error);
        }
        
        // Mark quest as completed in the daily quest service
        try {
          await dailyQuestService.markQuestCompleted(
            userId,
            selectedLesson.id.replace('daily-quest-', ''),
            selectedLesson.dailyQuestType,
            regularXp,
            DAILY_QUEST_BONUS,
            isPerfectLesson
          );
          console.log(`🎯 [Daily Quest] Quest marked as completed in service`);
        } catch (error) {
          console.error('Error marking quest as completed:', error);
        }
      }
      
      setIsCompletingQuest(false);
      return finalXp;
    },
    [completeLessonAndAwardXP]
  );

  return {
    completeDailyQuestAndAwardXP,
    isCompletingQuest
  };
};
